import api from './api';

export interface FiltrosEmpreendimentos {
  quartos?: number[];
  suites?: number[];
  precoMin?: number;
  precoMax?: number;
  areaMin?: number;
  areaMax?: number;
  municipio?: string[];
  bairro?: string[];
  tipoEmpreendimento?: number[];
  faseEmpreendimento?: number[];
  segmento?: number[];

  page?: number;
  limit?: number;
}

interface ApiResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// --- Converte arrays de strings/números para número ---
const parseNumberArray = (arr?: (number | string)[]): number[] | undefined => {
  if (!arr?.length) return undefined;
  const numbers = arr.map(x => Number(x)).filter(x => !isNaN(x));
  return numbers.length ? numbers : undefined;
};

// --- Busca empreendimentos com paginação ---
export const getEmpreendimentosPaginados = async (
  filtros: FiltrosEmpreendimentos,
  pagina: number = 1,
  itensPorPagina: number = 10
): Promise<ApiResponse<any>> => {
  const params = new URLSearchParams();

  // Paginação
  params.set('limit', itensPorPagina.toString());
  params.set('offset', ((pagina - 1) * itensPorPagina).toString());

  // Filtros
  if (filtros.precoMin !== undefined) params.set('preco_min', Math.floor(filtros.precoMin).toString());
  if (filtros.precoMax !== undefined) params.set('preco_max', Math.floor(filtros.precoMax).toString());
  if (filtros.areaMin !== undefined) params.set('area_min', Math.floor(filtros.areaMin).toString());
  if (filtros.areaMax !== undefined) params.set('area_max', Math.floor(filtros.areaMax).toString());

  const quartos = parseNumberArray(filtros.quartos);
  if (quartos) params.set('quartos', quartos.join(','));

  const suites = parseNumberArray(filtros.suites);
  if (suites) params.set('suites', suites.join(','));

  const tipos = parseNumberArray(filtros.tipoEmpreendimento);
  if (tipos) params.set('tipo_empreendimento', tipos.join(','));

  const fases = parseNumberArray(filtros.faseEmpreendimento);
  if (fases) params.set('fase_empreendimento', fases.join(','));

  const segmentos = parseNumberArray(filtros.segmento);
  if (segmentos) params.set('segmento', segmentos.join(','));

  if (filtros.municipio?.length) {
    const municipiosValidos = filtros.municipio.filter(v => v && v.trim() !== '');
    if (municipiosValidos.length) params.set('municipio', municipiosValidos.map(encodeURIComponent).join(','));
  }

  if (filtros.bairro?.length) {
    const bairrosValidos = filtros.bairro.filter(v => v && v.trim() !== '');
    if (bairrosValidos.length) params.set('bairro', bairrosValidos.map(encodeURIComponent).join(','));
  }

  try {
    const { data } = await api.get(`/tela empreendimento?${params.toString()}`);
    return data as ApiResponse<any>;
  } catch (error) {
    console.error('Erro ao buscar empreendimentos:', error);
    throw error;
  }
};

// --- Função para buscar UFs ---
export const getEstados = async () => {
  try {
    const { data } = await api.get('/ufs-base');
    return data; // [{ id: number, sigla: string, nome: string }]
  } catch (error) {
    console.error('Erro ao buscar estados:', error);
    throw error;
  }
};

// --- Função para buscar municípios pelo ID da UF ---
export const getMunicipios = async (ufId: number) => {
  try {
    const { data } = await api.get(`/municipios-base/por_uf?uf=${ufId}`);
    return data;
  } catch (error) {
    console.error(`Erro ao buscar municípios da UF ${ufId}:`, error);
    throw error;
  }
};

// --- Função para buscar bairros pelo ID do município ---
export const getBairros = async (municipioId: number) => {
  try {
    const { data } = await api.get(`/bairros-base/por_municipio?municipio=${municipioId}`);
    return data;
  } catch (error) {
    console.error(`Erro ao buscar bairros do município ${municipioId}:`, error);
    throw error;
  }
};
